import type { FC, FormEvent } from 'react';
import { useTranslation } from 'next-i18next';
import { selectUserIsAuthenticated, showModal, useQueryPopupOpen } from '@mwl/core-lib';
import { nanoid } from '@reduxjs/toolkit';
import CouponIcon from '@public/assets/promo/icons/coupon.svg';
import { useTypedDispatch, useTypedSelector } from '@/hooks';
import { BonusPacketCardPopupV2 } from '../BonusPacketCardPopupV2/BonusPacketCardPopupV2';
import { ButtonIcon } from '../ButtonIcon/ButtonIcon';
import type { FormFieldClasses } from '../FormField/FormField';
import { FormField } from '../FormField/FormField';
import { Loader } from '../Loader/Loader';
import { TooltipContentV2, TooltipTriggerV2, TooltipV2 } from '../TooltipV2/TooltipV2';
import { PromoFieldForm } from './components/PromoFieldForm/PromoFieldForm';
import type { PromoFieldProps } from './PromoField.types';
import { getPromoFieldActionIcon, getTooltipContent, strings } from './PromoField.utils';
import styles from './PromoField.module.scss';
export const PromoField: FC<PromoFieldProps> = ({
  name,
  analytics,
  currency,
  value,
  mode,
  className,
  classes,
  onChange,
  variant: i18nNamespace = 'common',
  maxLength = 16,
  disableInitialTooltip,
  tooltipVariant = 'black'
}) => {
  const {
    t
  } = useTranslation('common');
  const dispatch = useTypedDispatch();
  const isAuth = useTypedSelector(selectUserIsAuthenticated);
  const {
    createLinkProps
  } = useQueryPopupOpen();
  const formFieldClasses: FormFieldClasses = {
    component: cn(styles.input, classes?.component),
    componentWrapper: cn(styles.inputWrapper, classes?.componentWrapper)
  };
  return <PromoFieldForm mode={mode} maxLength={maxLength} currency={currency} className={cn(styles.root, className)} disableInitialTooltip={disableInitialTooltip} data-sentry-element="PromoFieldForm" data-sentry-component="PromoField" data-sentry-source-file="PromoField.tsx">
      {({
      formWidth,
      mutationState,
      form,
      isTooltipOpen,
      onSubmit
    }) => {
      const {
        formState,
        changeField
      } = form;
      const content = getTooltipContent(mutationState, createLinkProps, t, i18nNamespace);
      const ActionIcon = getPromoFieldActionIcon(mutationState);
      const handleSubmit = () => {
        if (!isAuth && mode === 'apply') {
          dispatch(showModal({
            id: nanoid(),
            content: <BonusPacketCardPopupV2 promocode={form.formState.values.promo} />
          }));
          return;
        }
        onSubmit(((new Event('submit') as unknown) as FormEvent<HTMLFormElement>));
      };
      const placeholder = t(strings[i18nNamespace].placeholder);
      const shouldOpenTooltip = isTooltipOpen && (isAuth || mutationState.isSuccess || !!mutationState.error);
      return <TooltipV2 open={shouldOpenTooltip} interactive placement="bottom">
            <TooltipTriggerV2>
              <div>
                <FormField as="input" analytics={analytics} name={name ?? 'promo'} inputMode="text" autoCapitalize="off" autoComplete="off" autoCorrect="off" spellCheck={false} prefix={<CouponIcon />} postfix={!formState.errors.promo && <ButtonIcon size="xs" color={mutationState.error ? 'red' : 'green'} as="button" type="button" disabled={mutationState.isLoading} aria-label="Apply voucher" onClick={mutationState.error ? undefined : handleSubmit}>
                        {mutationState.isLoading ? <Loader /> : <ActionIcon className={cn(styles.actionIcon, {
                [styles.actionIconError]: mutationState.error
              })} />}
                      </ButtonIcon>} className={cn(styles.formField, classes?.formField)} disabled={mutationState.isLoading} isSuccess={mutationState.isSuccess} classes={formFieldClasses} placeholder={placeholder} isError={formState.touched.promo && (!!formState.errors.promo || !!mutationState.error)} value={value ?? formState.values.promo} onChange={event => {
              if (event.target.value.length > maxLength) {
                event.preventDefault();
                return;
              }
              onChange?.(event);
              changeField('promo', event.target.value);
            }} />
              </div>
            </TooltipTriggerV2>
            <TooltipContentV2 className={styles.tooltipContent} style={{
          width: formWidth
        }} variant={tooltipVariant}>
              {!!content && isTooltipOpen && content}
            </TooltipContentV2>
          </TooltipV2>;
    }}
    </PromoFieldForm>;
};